import _ from 'lodash';
import { action, computed, IObservableArray, makeObservable, observable, runInAction } from 'mobx';
import { BnrDocumentModel, BnrDocumentStore } from '../bnr.document.store';
import { BnrUploadModel, BnrUploadStore } from '../bnr.upload.store';
import { SessionStore } from '../session.store';
import { BaseUiStore } from './base.ui.store';

interface INotizVM {
	currentBnrId: number;
	doc: BnrDocumentModel;
	uploads: BnrUploadModel[];
}

export class NotizVM {
	constructor(opts: INotizVM) {
		makeObservable(this);
		this.currentBnrId = opts.currentBnrId;
		this.doc = opts.doc;
		this.uploads = opts.uploads;
	}

	@observable
	isNew: boolean = false;

	currentBnrId: number;
	@observable
	doc: BnrDocumentModel;
	@observable
	uploads: BnrUploadModel[] = [];

	@computed
	get id() {
		return this.doc.id;
	}
}

export class NotizenUiStore extends BaseUiStore<NotizVM> {
	name = 'NotizenUiStore';
	bnrDocumentStore: BnrDocumentStore;
	session: SessionStore;
	bnrUploadStore: BnrUploadStore;
	constructor(session: SessionStore, bnrDocumentStore: BnrDocumentStore, bnrUploadStore: BnrUploadStore) {
		super();
		makeObservable(this);
		this.session = session;
		this.bnrDocumentStore = bnrDocumentStore;
		this.bnrUploadStore = bnrUploadStore;
	}

	loaded: boolean = false;


	@computed
	get realItemsForFuckSakes() {
		return this.items;
	}

	@computed
	get last5() {
		const items = _.sortBy(this.items, (i) => i.doc.modified).reverse();
		return items.slice(0, 4);
	}

	@action
	async _baseLoad() {
		const fetch = await Promise.all([this.bnrDocumentStore.findAll(), this.bnrUploadStore.findAll()]);
		let res: NotizVM[] = [];
		const docs = fetch[0];
		const uploads = fetch[1];

		docs.forEach((d) => {
			if (d.category !== 'user_notiz') {
				return;
			}
			// Filter out inline images - they are displayed in document content, not as attachments
			const docUploads = uploads.filter((u) => u.documentId === d.id && u.type !== 'inline-image');
			const vm = new NotizVM({
				currentBnrId: this.session.currentUser!.bnrId,
				doc: d,
				uploads: docUploads,
			});

			res.push(vm);
		});
		runInAction(() => {
			res = _.sortBy(res, (i) => i.doc.modified).reverse();
			this.items = res;
			this.loaded = true;
		});
	}

	async findAll() {
		await this.load();
		return this.items;
	}

	@action
	async refresh() {
		this.bnrUploadStore.loaded = false;
		this.clearCache();
		await this.load();
	}

	async findById(docId: string) {
		await this.load();
		return this.items.find((d) => d.id === docId);
	}

	@action
	async addNotiz() {
		const doc = new BnrDocumentModel({
			id: undefined,
			bnrId: this.session.currentUser!.bnrId,
			category: 'user_notiz',
			created: new Date(),
			modified: new Date(),
			editable: true,
			title: 'Notiz',
			status: 0,
			text: '',
		});

		const vm = new NotizVM({
			currentBnrId: this.session.currentUser!.bnrId,
			doc,
			uploads: [],
		});
		vm.isNew = true;
		vm.doc = await this.bnrDocumentStore.save(vm.doc);
		runInAction(() => {
			vm.doc.editable = true;
		});

		this.items.push(vm);

		return vm;
	}

	@action
	undoAdd() {
		this.items = this.items.filter((i) => !!i.id);
		this.currentModalDoc = undefined;
	}

	async deleteUpload(notiz: NotizVM, upload: BnrUploadModel) {
		await this.bnrUploadStore.deleteById(upload.id);
		runInAction(() => {
			notiz.uploads = notiz.uploads.filter((u) => u.id !== upload.id);
		});
	}

	async save(model: NotizVM) {
		const doc = await this.bnrDocumentStore.save(model.doc);
		model.doc = doc;
		// force reload, so that also new documents appear in the list
		//await this.refresh();

		return this.items.find((i) => i.id === doc.id);
	}

	@action
	async deleteById(id: string) {
		await this.bnrDocumentStore.deleteById(id);
		runInAction(() => {
			this.items = this.items.filter((d) => d.id !== id);
			this.setCurrentModalDoc(undefined);
		});
	}

	@observable currentModalDoc?: NotizVM;

	@action
	setCurrentModalDoc(doc?: NotizVM) {
		this.currentModalDoc = doc;
	}
	@action
	setPreviousModalDoc() {
		if (!this.currentModalDoc || this.items.length === 0) {
			return;
		}
		let i = this.items.findIndex((d) => d.id === this.currentModalDoc!.id);
		if (i === 0) {
			i = this.items.length;
		}
		const d = this.items[i - 1];
		this.setCurrentModalDoc(d);
	}

	@action
	setNextModalDoc() {
		if (!this.currentModalDoc || this.items.length === 0) {
			return;
		}
		let i = this.items.findIndex((d) => d.id === this.currentModalDoc!.id);
		if (i === this.items.length) {
			i = 0;
		}
		const d = this.items[i + 1];
		this.setCurrentModalDoc(d);
	}
}
